import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import useSWRFetcher, { SWRFetchError } from "./fetcher";
import { Boards } from "../../../components/CustomerBoards/constants";
import useSWR from "swr";
import settings from "../../../constants/settings";
import useFetchAndTrace from "../fetchWithTrace";
import { QuoteState } from "../../../reducers/uploadedBoard";
import useSendBoardEvent from "./uploaded-board/state-machine/sendBoardEvent";

interface CustomerBoardFilters {
  page?: number;
  userId?: string;
  boardId?: string;
  quoteState?: QuoteState | null;
  awaitingCustomQuote?: boolean;
  boardProjectName?: string;
}

export function parseStateQuery<State>(
  queryString: string | undefined,
): State | undefined {
  if (typeof queryString === "string") {
    if (queryString === "") {
      return undefined;
    }
    return queryString as State;
  }
}

export const useCustomerBoards = () => {
  const router = useRouter();
  const page = parseInt((router.query.page as string | undefined) || "1");
  const userId = router.query.userId as string | undefined;
  const boardId = router.query.boardId as string | undefined;
  const quoteState = parseStateQuery<QuoteState>(
    router.query.quoteState as string | undefined,
  );
  const awaitingCustomQuote = router.query.awaitingCustomQuote == "true";
  const boardProjectName = router.query.boardProjectName as string | undefined;
  const [errored, setErrored] = useState(false);
  const [anonymizing, setAnonymizing] = useState(false);
  const fetchAndTrace = useFetchAndTrace();

  const errorHandler = useCallback(() => {
    setAnonymizing(false);
    setErrored(true);
  }, []);

  const fetcher = useSWRFetcher<Boards>(undefined);
  const {
    data: boards,
    mutate,
    isValidating,
    error,
  } = useSWR<Boards, SWRFetchError>(
    `${settings.api_url}/boards?page=${page}&user_id=${userId || ""}&board_id=${
      boardId || ""
    }&quote_state=${quoteState || ""}&awaiting_custom_quote=${awaitingCustomQuote}&board_project_name=${boardProjectName || ""}`,
    fetcher,
    { revalidateOnFocus: false },
  );

  const refreshBoards = useCallback(() => {
    void mutate();
  }, [mutate]);

  const { sendBoardEvent, loading: sendingBoardEvents } = useSendBoardEvent(
    errorHandler,
    refreshBoards,
  );

  const loading = isValidating || sendingBoardEvents || anonymizing;

  const updateFilters = useCallback(
    (options: CustomerBoardFilters) => {
      void router.push(
        {
          pathname: "/internal/boards",
          query: {
            page: options.page !== undefined ? options.page : page,
            userId: options.userId !== undefined ? options.userId : userId,
            boardId: options.boardId !== undefined ? options.boardId : boardId,
            quoteState:
              options.quoteState !== undefined
                ? options.quoteState
                : quoteState,
            awaitingCustomQuote:
              options.awaitingCustomQuote !== undefined
                ? options.awaitingCustomQuote
                : awaitingCustomQuote,
            boardProjectName:
              options.boardProjectName !== undefined
                ? options.boardProjectName
                : boardProjectName,
          },
        },
        undefined,
        { scroll: false },
      );
    },
    [
      router,
      page,
      userId,
      boardId,
      quoteState,
      awaitingCustomQuote,
      boardProjectName,
    ],
  );

  const clearFilters = () => {
    void router.push(
      {
        pathname: "/internal/boards",
      },
      undefined,
      { scroll: false },
    );
  };

  useEffect(() => {
    if (boards && boards.boards.length == 0 && page != 1) {
      updateFilters({ page: 1 });
    }
  }, [boards, page, updateFilters]);

  useEffect(() => {
    if (error !== undefined) {
      errorHandler();
    }
  }, [error, errorHandler]);

  const processBoardEventHandler = (response: Response) => {
    if (response.ok) {
      refreshBoards();
      setAnonymizing(false);
    } else {
      throw Error("Bad response");
    }
  };

  const anonymizeBoard = (boardId: string) => {
    if (confirm("Are you sure you want to anonymize this board?")) {
      setAnonymizing(true);
      void fetchAndTrace(
        `${settings.api_url}/boards/${boardId}/owner`,
        processBoardEventHandler,
        errorHandler,
        {
          method: "PUT",
        },
      );
    }
  };

  return {
    boards,
    refreshBoards,
    loading,
    errored,
    sendBoardEvents: sendBoardEvent,
    updateFilters,
    clearFilters,
    filters: {
      page,
      userId,
      boardId,
      quoteState,
      awaitingCustomQuote,
      boardProjectName,
    },
    anonymizeBoard,
  };
};
